import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, Loader2, Info, CheckCircle2, CalendarX } from "lucide-react";
import { api } from "@/lib/api";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // Python weekday(): Mon=0..Sun=6
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function shortSlot(label) {
  // "Morning (08:00–12:00)" -> { name: "Morning", time: "08:00–12:00" }
  const m = label.match(/^(.*?)\s*\((.*)\)\s*$/);
  if (m) return { name: m[1].trim(), time: m[2].trim() };
  return { name: label, time: "" };
}

/**
 * Reusable availability picker. Controlled component.
 * props:
 *  - value: { date, slot }
 *  - onChange: ({ date, slot }) => void
 *  - compact: boolean (tighter spacing)
 */
export const AvailabilityPicker = ({ value = {}, onChange, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/availability")
      .then(({ data }) => {
        if (active) {
          setData(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Group available days by month for a clean scannable layout
  const grouped = useMemo(() => {
    if (!data?.days) return [];
    const groups = {};
    for (const day of data.days) {
      const [y, mo] = day.date.split("-");
      const key = `${y}-${mo}`;
      if (!groups[key]) groups[key] = { label: `${MONTH_LABELS[Number(mo) - 1]} ${y}`, days: [] };
      groups[key].days.push(day);
    }
    return Object.values(groups);
  }, [data]);

  const selectedDay = useMemo(
    () => data?.days?.find((d) => d.date === value.date) || null,
    [data, value.date]
  );

  const pickDate = (date) => {
    if (date === value.date) return;
    onChange?.({ date, slot: "" }); // reset slot when date changes
  };
  const pickSlot = (slot) => onChange?.({ date: value.date, slot });

  if (loading) {
    return (
      <div className="ascendia-card p-6 flex items-center gap-3 text-muted-c" data-testid="availability-loading">
        <Loader2 className="h-4 w-4 animate-spin text-gold-soft" /> Loading available windows…
      </div>
    );
  }

  if (error) {
    return (
      <div className="ascendia-card p-6 text-sm text-muted-c" data-testid="availability-error">
        We couldn't load the calendar right now. You can still submit your request and we'll confirm a time with you directly.
      </div>
    );
  }

  if (!data?.booking_enabled || !data?.days?.length) {
    return (
      <div className="ascendia-card p-6 flex items-start gap-3" data-testid="availability-none">
        <CalendarX className="h-5 w-5 text-gold-soft flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-white font-medium">No open windows to show right now</p>
          <p className="mt-1 text-xs text-muted-c">
            {data?.note || "Please submit your request and our team will confirm a time with you directly."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="availability-picker" className="space-y-5">
      <div className="flex items-start gap-2 rounded-lg bg-black/30 border border-[color:var(--border-subtle)] p-3">
        <Info className="h-4 w-4 text-gold-soft flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-c">{data.note}</p>
      </div>

      {/* Date selection */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="h-4 w-4 text-gold-soft" />
          <span className="text-sm text-secondary font-medium">Choose a day</span>
        </div>
        <div className={`space-y-4 ${compact ? "max-h-64 overflow-y-auto pr-1" : ""}`}>
          {grouped.map((g) => (
            <div key={g.label}>
              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-c mb-2">{g.label}</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {g.days.map((day) => {
                  const d = new Date(day.date + "T00:00:00");
                  const dayNum = d.getDate();
                  const on = value.date === day.date;
                  return (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => pickDate(day.date)}
                      data-testid={`availability-date-${day.date}`}
                      aria-pressed={on}
                      className={`flex flex-col items-center rounded-xl border px-2 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${
                        on
                          ? "border-gold bg-gold/10 text-white"
                          : "border-[color:var(--border-subtle)] text-secondary hover:bg-white/[0.03] hover:border-[color:var(--border-strong)]"
                      }`}
                    >
                      <span className="text-[10px] uppercase tracking-wide text-muted-c">{WEEKDAY_LABELS[day.weekday]}</span>
                      <span className={`text-lg font-display font-semibold ${on ? "text-gold" : "text-white"}`}>{dayNum}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slot selection */}
      {selectedDay && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-gold-soft" />
            <span className="text-sm text-secondary font-medium">Choose a time window</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-2.5">
            {selectedDay.slots.map((s) => {
              const { name, time } = shortSlot(s.label);
              const on = value.slot === s.label;
              const disabled = !s.available;
              return (
                <button
                  key={s.label}
                  type="button"
                  disabled={disabled}
                  onClick={() => pickSlot(s.label)}
                  data-testid={`availability-slot-${name.toLowerCase()}`}
                  aria-pressed={on}
                  className={`flex items-center justify-between rounded-xl border p-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${
                    disabled
                      ? "border-[color:var(--border-subtle)] opacity-40 cursor-not-allowed"
                      : on
                      ? "border-gold bg-gold/10"
                      : "border-[color:var(--border-subtle)] hover:bg-white/[0.03] hover:border-[color:var(--border-strong)]"
                  }`}
                >
                  <span>
                    <span className="block text-sm font-medium text-white">{name}</span>
                    <span className="block text-xs text-muted-c">{time}</span>
                  </span>
                  {on ? (
                    <CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0" />
                  ) : disabled ? (
                    <span className="text-[10px] text-muted-c">Full</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.date && value.slot && (
        <div className="flex items-center gap-2 text-sm text-gold-soft" data-testid="availability-selection-summary">
          <CheckCircle2 className="h-4 w-4" />
          <span>
            Requested:{" "}
            <span className="text-white font-medium">
              {new Date(value.date + "T00:00:00").toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </span>{" "}
            · {shortSlot(value.slot).name} ({shortSlot(value.slot).time})
          </span>
        </div>
      )}
    </div>
  );
};

export default AvailabilityPicker;
